import React, { useState } from 'react';
import { BsHddNetwork } from 'react-icons/bs';

// Guía, paso a paso y con comandos, para preparar UN proyecto en su VPS una vez instalado el
// agente de ajustes. Vive en la app para no depender de la memoria: dentro de un mes, con un
// proyecto nuevo, se abre esta ventana y se sigue en orden.

const Block = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_e) { /* selección manual */ }
  };
  return (
    <div className="position-relative mb-2">
      <button type="button" className={`btn btn-sm position-absolute ${copied ? 'btn-success' : 'btn-outline-light'}`} style={{ top: 6, right: 6 }} onClick={copy}>{copied ? '✓' : 'Copiar'}</button>
      <pre className="bg-dark text-light p-3 rounded mb-0" style={{ fontSize: '0.74rem', whiteSpace: 'pre', overflow: 'auto' }}>{code}</pre>
    </div>
  );
};

const slugify = (s) => String(s || 'proyecto').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 24) || 'proyecto';

const ProjectSetupModal = ({ connection, onClose }) => {
  const staging = Boolean(connection.staging_url);
  const id = connection.project_id;
  const [vars, setVars] = useState({
    prodDir: '/var/www/cliente',
    prodDb: slugify(connection.project_title).replace(/-/g, '_'),
    stgDomain: (connection.staging_url || '').replace(/^https?:\/\//, '').replace(/\/.*$/, '') || 'pruebas.cliente.com'
  });
  const set = (k) => (e) => setVars((p) => ({ ...p, [k]: e.target.value }));
  const stgDir = `${vars.prodDir.replace(/\/$/, '')}-staging`;
  const stgDb = `${vars.prodDb}_stg`;

  const step = (n, title, children) => (
    <div className="mb-4">
      <h6 className="mb-2"><span className="badge bg-primary rounded-pill me-2">{n}</span>{title}</h6>
      {children}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', overflowY: 'auto' }}>
      <div className="card" style={{ width: '100%', maxWidth: 900 }}>
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong><BsHddNetwork className="me-2" />Configurar «{connection.project_title}» en el servidor {connection.server_name ? `«${connection.server_name}»` : ''}</strong>
          <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
        </div>
        <div className="card-body">
          <p className="small">
            Antes hace falta tener el <strong>agente de ajustes instalado</strong> en el servidor (pestaña <strong>Servidores</strong> → Instalar agente). Esto de aquí se hace <strong>una vez por proyecto</strong>, por SSH. Rellena estos tres datos y los comandos se adaptan:
          </p>
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <label className="form-label small mb-1">Carpeta de la web en producción</label>
              <input className="form-control form-control-sm font-monospace" value={vars.prodDir} onChange={set('prodDir')} />
            </div>
            <div className="col-md-4">
              <label className="form-label small mb-1">Base de datos de producción</label>
              <input className="form-control form-control-sm font-monospace" value={vars.prodDb} onChange={set('prodDb')} />
            </div>
            <div className="col-md-4">
              <label className="form-label small mb-1">Dominio de staging</label>
              <input className="form-control form-control-sm font-monospace" value={vars.stgDomain} onChange={set('stgDomain')} disabled={!staging} />
            </div>
          </div>

          {!staging && (
            <div className="alert alert-warning small">
              Este proyecto está conectado <strong>sin dirección de staging</strong>: Claude trabajará directamente sobre la web real (tras hacer backup) y no hay paso de publicar. Es más arriesgado. Si prefieres staging, edita la conexión y pon su dirección: los pasos de abajo cambiarán.
            </div>
          )}

          {staging && step(1, 'Copia de staging (donde trabaja Claude y prueba el cliente)', (
            <>
              <div className="small mb-2">Una copia de la web como repositorio git, con <strong>su propia base de datos</strong>. El dueño de la carpeta debe ser <code>claudebot</code>; la carpeta de producción no, así Claude no puede tocarla.</div>
              <Block code={`# Como root. Copia del código (sin caché ni subidas pesadas si no hacen falta)
sudo rsync -a --exclude 'var/cache' --exclude 'cache/' ${vars.prodDir}/ ${stgDir}/
sudo chown -R claudebot:claudebot ${stgDir}
sudo chmod 750 ${vars.prodDir}            # producción: claudebot no puede ni leerla

# Copia de la base de datos
sudo mysql -e "CREATE DATABASE IF NOT EXISTS ${stgDb} CHARACTER SET utf8mb4"
sudo mysqldump --single-transaction ${vars.prodDb} | sudo mysql ${stgDb}

# Repositorio git en la copia (si la web no lo era ya)
sudo -u claudebot bash -c "cd ${stgDir} && git init -q -b main 2>/dev/null; git add -A && git commit -qm 'Estado inicial de staging' && git checkout -q -b ajustes"`} />
              <div className="small text-muted">
                Después, en la configuración de la copia (su <code>.env</code>, <code>parameters.php</code>, <code>wp-config.php</code>…) apunta a la base de datos <code>{stgDb}</code>, pon la <strong>pasarela de pago en modo test</strong> y <strong>desactiva los envíos de email</strong>, para que una prueba no cobre ni escriba a clientes reales. Crea el dominio <code>{vars.stgDomain}</code> en tu panel (Plesk, HestiaCP…) apuntando a <code>{stgDir}</code> y protégelo con usuario y contraseña: esa contraseña es la que le das al cliente para probar.
              </div>
            </>
          ))}

          {step(staging ? 2 : 1, 'Usuario MySQL para Claude', (
            <>
              <div className="small mb-2">Solo sobre {staging ? 'la base de datos de staging' : 'la base de datos del proyecto'} y sin poder borrar tablas. Claude lo usa para los cambios de datos (importaciones, correcciones).</div>
              <Block code={`sudo mysql <<'SQL'
CREATE USER IF NOT EXISTS 'claude_${slugify(connection.project_title).replace(/-/g, '_').slice(0, 12)}'@'localhost' IDENTIFIED BY 'CAMBIA-ESTA-CONTRASEÑA';
GRANT SELECT, INSERT, UPDATE, DELETE, CREATE, ALTER, INDEX, CREATE TEMPORARY TABLES ON ${staging ? stgDb : vars.prodDb}.* TO 'claude_${slugify(connection.project_title).replace(/-/g, '_').slice(0, 12)}'@'localhost';
SQL
# Credenciales para que claudebot no tenga que escribirlas (si ya existe el fichero, añade solo este bloque)
sudo -u claudebot tee -a /home/claudebot/.my.cnf > /dev/null <<'CNF'
[client]
user=claude_${slugify(connection.project_title).replace(/-/g, '_').slice(0, 12)}
password=CAMBIA-ESTA-CONTRASEÑA
CNF
sudo chmod 600 /home/claudebot/.my.cnf`} />
              <div className="small text-muted">Si el servidor tiene varios proyectos, cada uno con su usuario: en <code>.my.cnf</code> solo cabe uno por defecto, así que en ese caso pon las credenciales en <code>BACKUP_CMD</code> y en el <code>CLAUDE.md</code> del proyecto (<code>mysql --defaults-group-suffix=…</code>).</div>
            </>
          ))}

          {step(staging ? 3 : 2, `Configuración del proyecto en el servidor (/opt/ajustes/proyectos/${id}.env)`, (
            <>
              <div className="small mb-2">Es lo que le dice al agente dónde está el proyecto. El <strong>id {id}</strong> es el de este proyecto en Automationia: el agente rechaza cualquier trabajo de un proyecto sin este fichero.</div>
              <Block code={`sudo tee /opt/ajustes/proyectos/${id}.env > /dev/null <<'ENV'
# ${connection.project_title} (proyecto ${id} de Automationia)
PROJECT_DIR=${staging ? stgDir : vars.prodDir}
ENTORNO=${staging ? 'staging' : 'produccion'}
GIT_BRANCH=ajustes
BACKUP_CMD="mysqldump --single-transaction ${staging ? stgDb : vars.prodDb}"
# Herramientas extra para Claude según el stack (cada una puede ejecutar código como claudebot):
# ALLOWED_TOOLS="Read,Edit,Write,Bash(git add *),Bash(git commit *),Bash(git status *),Bash(git diff *),Bash(git log *),Bash(mysql *),Bash(php artisan *)"
# POST_APPLY_CMD="php artisan optimize:clear"
${staging ? `
# Publicación a producción (la ejecuta el usuario de despliegue)
PUBLISH_CMD=/opt/ajustes/deploy/${id}.sh
PROD_DIR=${vars.prodDir}
PROD_DB=${vars.prodDb}
# POST_PUBLISH_CMD="php artisan optimize"` : '# Sin staging: no hay publicación; Claude aplica directamente tras el backup.'}
ENV
sudo chgrp ajustes /opt/ajustes/proyectos/${id}.env && sudo chmod 640 /opt/ajustes/proyectos/${id}.env`} />
            </>
          ))}

          {step(staging ? 4 : 3, 'Reglas para Claude en el repositorio (CLAUDE.md)', (
            <>
              <div className="small mb-2">Un fichero en la raíz de la copia con el stack, cómo probar y qué no tocar. Claude lo lee antes de cada ajuste y manda sobre lo que pida el cliente.</div>
              <Block code={`sudo -u claudebot cp /opt/ajustes/CLAUDE.md.ejemplo ${staging ? stgDir : vars.prodDir}/CLAUDE.md
sudo -u claudebot nano ${staging ? stgDir : vars.prodDir}/CLAUDE.md     # rellena lo que va entre corchetes
sudo -u claudebot bash -c "cd ${staging ? stgDir : vars.prodDir} && git add CLAUDE.md && git commit -qm 'Reglas para ajustes'"`} />
            </>
          ))}

          {staging && step(5, 'Script de publicación a producción', (
            <>
              <div className="small mb-2">Parte del ejemplo: hace backup de producción, lleva el código de staging hasta el commit del ajuste, ejecuta los SQL nuevos de <code>db/cambios/</code> y limpia caché. Ajusta el final a su stack.</div>
              <Block code={`sudo cp /opt/ajustes/deploy-ejemplo.sh /opt/ajustes/deploy/${id}.sh
sudo chown <usuario-despliegue> /opt/ajustes/deploy/${id}.sh && sudo chmod 750 /opt/ajustes/deploy/${id}.sh
sudo nano /opt/ajustes/deploy/${id}.sh
# El usuario de despliegue tiene que poder leer el repo de staging:
sudo -u <usuario-despliegue> git config --global --add safe.directory ${stgDir}
# Y producción debe ser un repositorio git con el mismo historial (una vez):
sudo -u <usuario-despliegue> bash -c "cd ${vars.prodDir} && git init -q -b main 2>/dev/null; git fetch -q ${stgDir} main && git reset -q --soft FETCH_HEAD && git commit -qm 'Estado inicial de producción' --allow-empty"`} />
            </>
          ))}

          {step(staging ? 6 : 4, 'Probar', (
            <>
              <div className="small mb-2">Crea un ajuste de prueba para este proyecto (por ejemplo «cambia el texto del pie por …»), pulsa <strong>Enviar a Claude</strong> y lanza el agente a mano en vez de esperar al cron:</div>
              <Block code={`sudo -u claudebot /opt/ajustes/runner.sh
ls -t /opt/ajustes/runs/ | head -1      # carpeta del trabajo: ajuste.md, adjuntos y la respuesta de Claude${staging ? `
# Cuando esté «Listo en staging», míralo en https://${vars.stgDomain}, pulsa Publicar y:
sudo -u <usuario-despliegue> /opt/ajustes/publish.sh` : ''}`} />
              <div className="small text-muted">Si algo falla, el motivo sale en el ajuste (Trabajos en el servidor → Ver log) y en <code>/var/log/ajustes-runner.log</code>.</div>
            </>
          ))}
        </div>
        <div className="card-footer d-flex justify-content-end">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
};

export default ProjectSetupModal;
